import * as AppStore from './../../state/app-store'
import { Avatar, Text, Timeline } from '@mantine/core'
import pluralize from 'pluralize'

import * as Types from '../../declarations'
import {
	formatDistance,
	formatHeight,
	formatNumberOrRange,
} from '../../lib/display'
import * as Selectors from './../../state/selectors'
import SectionHeader from './components/SectionHeader'
import HTMLContent from 'components/HTMLContent'

interface Props {
	trip: Types.DB.Models.TripAttributes
}

const Itinerary = ({
	trip: {
		name,
		jsonData: { itinerary, trekOrClimb },
	},
}: Props) => {
	if (!itinerary) return <></>

	const unit = AppStore.useAppSelector(Selectors.unitSelector)

	const isTrek = trekOrClimb === Types.App.DataTypes.TrekOrClimb.TREK
	const isClimb = trekOrClimb === Types.App.DataTypes.TrekOrClimb.CLIMB

	return (
		<div id="itinerary-wrapper" className="spacing-between-sections-above">
			<SectionHeader
				title={`${name} Itinerary`}
				section={Types.App.DataTypes.FeedbackSection.ITINERARY}
			/>
			{itinerary.description && (
				<HTMLContent
					id="itinerary-description"
					html={itinerary.description}
				></HTMLContent>
			)}

			{isTrek && itinerary?.days && (
				<div id="itinerary-days">
					{itinerary.days.map((day, key) => {
						return (
							<div className="itinerary-day-item" key={key}>
								<div className="itinerary-day-number">Day {key + 1}</div>
								<div className="itinerary-day-remainder">
									<h3>
										{day.title}{' '}
										{!!day?.destinationElevation && (
											<b>
												<small>
													({formatHeight([day.destinationElevation], unit)})
												</small>
											</b>
										)}
									</h3>

									{day?.totalElevationChange ||
									day?.totalTime ||
									day?.distance ? (
										<div className="individual-day-stats">
											{!!day?.distance && (
												<span>
													Distance:{' '}
													<b>{formatDistance([day.distance], unit)}</b>
												</span>
											)}

											{!!day?.totalTime && day?.totalTime !== -1 && (
												<span>
													Time trekking / climbing:{' '}
													<b>
														{formatNumberOrRange([day.totalTime as number])}
														&nbsp;{pluralize('hour', day.totalTime)}
													</b>
												</span>
											)}

											{!!day?.totalElevationChange && (
												<span>
													Elevation change:{' '}
													<b>
														{day.totalElevationChange > 0 ? '+' : ''}
														{formatHeight([day.totalElevationChange], unit)}
													</b>
												</span>
											)}
										</div>
									) : (
										<></>
									)}
									{day?.description && (
										<HTMLContent
											html={day.description}
											className="description"
										></HTMLContent>
									)}
								</div>
							</div>
						)
					})}
				</div>
			)}

			{isClimb && itinerary?.climbSections && (
				<div className="spacing-between-sections-above">
					<Timeline
						active={itinerary.climbSections.length}
						lineWidth={4}
						bulletSize={24}
					>
						{itinerary.climbSections.map((item, key) => (
							<Timeline.Item
								title={
									<>
										<h3>
											<Avatar
												size={24}
												radius="xl"
												alt={(() => {
													if (item.isSummit && !item.isBaseCamp) {
														return 'summit'
													}
													if (item.isBaseCamp) {
														return 'base-camp'
													}

													if (!item.isBaseCamp && !item.isSummit) {
														return 'high-camp'
													}
													return 'itinerary-stop'
												})()}
												src={(() => {
													if (item.isSummit && !item.isBaseCamp) {
														return '/summit.png'
													}
													if (item.isBaseCamp) {
														return '/base-camp.png'
													}

													if (!item.isBaseCamp && !item.isSummit) {
														return '/high-camp.png'
													}
												})()}
												style={{
													display: 'inline-block',
													marginRight: 8,
													verticalAlign: 'text-bottom',
												}}
											/>
											{item.name}
										</h3>
									</>
								}
								key={key}
							>
								<Text color="dimmed" size="sm">
									{item.description}
								</Text>
								<Text size="xs" mt={4}>
									{formatHeight([item.elevation], unit)}
								</Text>
							</Timeline.Item>
						))}
					</Timeline>
				</div>
			)}
		</div>
	)
}

export default Itinerary
